<template>
  <div id="app" class="container-fluid">
    <full_head />

    <div class="main-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading patient profile...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button class="btn-retry" @click="fetchPatientProfile">Retry</button>
      </div>

      <!-- Content -->
      <div v-else class="row g-3">
        <!-- Left Column - Personal Information -->
        <div class="col-lg-6">
          <div class="card">
            <div class="card-header">
              <h3>
                <i class="fas fa-user-circle"></i>
                Personal Info
              </h3>
              <button class="btn-edit-header" @click="showEditProfileModal">
                <i class="fas fa-edit"></i>
                Edit Profile
              </button>
            </div>
            <div class="card-body">
              <div class="info-list">
                <div class="info-row">
                  <span class="label">Full Name</span>
                  <span class="value">{{ patient.fullName || "N/A" }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Age</span>
                  <span class="value">{{
                    patient.age ? patient.age + " yrs" : "N/A"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Date of Birth</span>
                  <span class="value">{{ patient.dateOfBirth || "N/A" }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Marital Status</span>
                  <span class="value">{{
                    patient.maritalStatus || "N/A"
                  }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Education</span>
                  <span class="value">{{ patient.education || "N/A" }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Occupation</span>
                  <span class="value">{{ patient.occupation || "N/A" }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Husband's Name</span>
                  <span class="value">{{ patient.husbandName || "N/A" }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Husband's Job</span>
                  <span class="value">{{
                    patient.husbandOccupation || "N/A"
                  }}</span>
                </div>
                <div class="info-row info-row-full">
                  <span class="label">Address</span>
                  <span class="value">{{ patient.address || "N/A" }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Phone</span>
                  <span class="value">{{ patient.phone || "N/A" }}</span>
                </div>
                <div class="info-row">
                  <span class="label">Emergency Contact</span>
                  <span class="value">{{
                    patient.emergencyContact || "N/A"
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Obstetric History -->
        <div class="col-lg-6">
          <!-- Current Pregnancy Card -->
          <div class="card">
            <div class="card-header">
              <h3>
                <i class="fas fa-calendar-check"></i>
                Current Pregnancy
              </h3>
            </div>
            <div class="card-body">
              <div class="pregnancy-stats">
                <div class="stat-item">
                  <span class="stat-label">LMP</span>
                  <span class="stat-value">{{
                    formatDate(pregnancyInfo.lmp)
                  }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">EDD</span>
                  <span class="stat-value">{{
                    formatDate(pregnancyInfo.edd)
                  }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Gestational Age</span>
                  <span class="stat-value"
                    >{{ pregnancyInfo.gestationalAge || 0 }} wks</span
                  >
                </div>
                <div class="stat-item">
                  <span class="stat-label">ANC Visits</span>
                  <span class="stat-value">{{
                    pregnancyInfo.ancVisits || 0
                  }}</span>
                </div>
              </div>

              <div v-if="hasPregnancyData" class="pregnancy-details">
                <div style="border-right: 1px solid grey" class="detail-row">
                  <span class="detail-label">Blood Group</span>
                  <span class="detail-value">{{
                    pregnancyInfo.bloodGroup || "N/A"
                  }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Rhesus Factor</span>
                  <span class="detail-value">{{
                    pregnancyInfo.rhesusFactor || "N/A"
                  }}</span>
                </div>
                <div style="border-right: 1px solid grey" class="detail-row">
                  <span class="detail-label">HIV Status</span>
                  <span class="detail-value">
                    <span
                      :class="
                        pregnancyInfo.hivStatus === 'Negative'
                          ? 'status-negative'
                          : pregnancyInfo.hivStatus === 'Positive'
                          ? 'status-positive'
                          : ''
                      "
                    >
                      {{ pregnancyInfo.hivStatus || "N/A" }}
                    </span>
                  </span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">VDRL/RPR</span>
                  <span class="detail-value">{{
                    pregnancyInfo.vdrl || "N/A"
                  }}</span>
                </div>
                <div style="border-right: 1px solid grey" class="detail-row">
                  <span class="detail-label">HB Level</span>
                  <span class="detail-value"
                    >{{ pregnancyInfo.hbLevel || "N/A" }} g/dL</span
                  >
                </div>
                <div class="detail-row">
                  <span class="detail-label">Tetanus Doses</span>
                  <span class="detail-value"
                    >{{ pregnancyInfo.tetanusCompleted || 0 }}/{{
                      pregnancyInfo.tetanusTotal || 5
                    }}</span
                  >
                </div>
                <div style="border-right: 1px solid grey" class="detail-row">
                  <span class="detail-label">Last Visit</span>
                  <span class="detail-value">{{
                    formatDate(pregnancyInfo.lastVisit)
                  }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">Next Appointment</span>
                  <span class="detail-value">{{
                    formatDate(pregnancyInfo.nextAppointment)
                  }}</span>
                </div>
              </div>
              <div v-else class="no-data">No pregnancy data available</div>
            </div>
          </div>

          <!-- Obstetric History Card -->
          <div class="card">
            <div class="card-header">
              <h3>
                <i class="fas fa-baby-carriage"></i>
                Obstetric History
              </h3>
            </div>
            <div class="card-body">
              <div class="gp-score">
                <div class="gp-item">
                  <span class="gp-label">Gravida</span>
                  <span class="gp-value">{{
                    obstetricHistory.gravida || 0
                  }}</span>
                </div>
                <div class="gp-item">
                  <span class="gp-label">Para</span>
                  <span class="gp-value">{{ obstetricHistory.para || 0 }}</span>
                </div>
                <div class="gp-item">
                  <span class="gp-label">Abortions</span>
                  <span class="gp-value">{{
                    obstetricHistory.abortions || 0
                  }}</span>
                </div>
                <div class="gp-item">
                  <span class="gp-label">Living</span>
                  <span class="gp-value">{{
                    obstetricHistory.livingChildren || 0
                  }}</span>
                </div>
              </div>

              <h4 class="section-title">Previous Pregnancies</h4>
              <div
                v-if="obstetricHistory.previousPregnancies.length"
                class="table-responsive"
              >
                <table class="table-custom">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Outcome</th>
                      <th>Weeks</th>
                      <th>Weight</th>
                      <th>Delivery</th>
                      <th>Complications</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(
                        pregnancy, index
                      ) in obstetricHistory.previousPregnancies"
                      :key="index"
                    >
                      <td>{{ pregnancy.year || "N/A" }}</td>
                      <td>
                        <span
                          class="badge-outcome"
                          :class="getOutcomeClass(pregnancy.outcome)"
                        >
                          {{ pregnancy.outcome || "N/A" }}
                        </span>
                      </td>
                      <td>
                        {{
                          pregnancy.gestation
                            ? pregnancy.gestation + " wks"
                            : "N/A"
                        }}
                      </td>
                      <td>
                        {{
                          pregnancy.birthWeight
                            ? pregnancy.birthWeight + " kg"
                            : "N/A"
                        }}
                      </td>
                      <td>{{ pregnancy.deliveryMode || "N/A" }}</td>
                      <td>
                        <span
                          v-if="pregnancy.complications"
                          class="complication-badge"
                        >
                          <i class="fas fa-exclamation-triangle"></i> Yes
                        </span>
                        <span v-else class="text-muted">None</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="no-data">No previous pregnancy history</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <anc_profile_editor
      v-if="showEditModal"
      :visitId="visitId"
      @close-edit-patient="handleCloseEditModal"
      @save="handleSaveProfile"
    />
  </div>
</template>

<script>
import full_head from "./full_head.vue";
// import anc_profile_editor from "./anc_profile_editor.vue";
import anc_profile_editor from "../../anc/anc_profile_editor.vue";
// import { generalApiRequest } from "../../../../api/apiIndex";
import { generalApiRequest } from "../../../../../api/apiIndex";

export default {
  name: "MaternalProfile",
  components: { full_head, anc_profile_editor },
  data() {
    return {
      showEditModal: false,
      loading: false,
      error: null,
      visitId: null,
      patient: {
        fullName: "",
        age: null,
        dateOfBirth: "",
        maritalStatus: "",
        education: "",
        occupation: "",
        husbandName: "",
        husbandOccupation: "",
        address: "",
        phone: "",
        emergencyContact: "",
      },
      pregnancyInfo: {
        lmp: "",
        edd: "",
        gestationalAge: null,
        ancVisits: null,
        bloodGroup: "",
        rhesusFactor: "",
        hivStatus: "",
        vdrl: "",
        hbLevel: null,
        tetanusCompleted: 0,
        tetanusTotal: 5,
        lastVisit: "",
        nextAppointment: "",
      },
      obstetricHistory: {
        gravida: 0,
        para: 0,
        abortions: 0,
        livingChildren: 0,
        previousPregnancies: [],
      },
    };
  },
  computed: {
    hasPregnancyData() {
      return (
        this.pregnancyInfo.lmp ||
        this.pregnancyInfo.bloodGroup ||
        this.pregnancyInfo.hivStatus
      );
    },
  },
  mounted() {
    // Get visit_id from route params
    this.visitId = this.$route.params.visit_id;

    if (this.visitId) {
      this.fetchPatientProfile();
    } else {
      this.error = "No visit ID provided";
    }
  },
  methods: {
    async fetchPatientProfile() {
      this.loading = true;
      this.error = null;

      try {
        const url = "get_anc_profile/" + this.visitId;
        const response = await generalApiRequest(url, "GET", {}, 1);

        if (response.success && response.data) {
          // Populate patient data
          if (response.data.patient) {
            this.patient = { ...this.patient, ...response.data.patient };
          }

          // Populate pregnancy info
          if (response.data.pregnancyInfo) {
            this.pregnancyInfo = {
              ...this.pregnancyInfo,
              ...response.data.pregnancyInfo,
            };
          }

          // Populate obstetric history
          if (response.data.obstetric) {
            this.obstetricHistory = {
              ...this.obstetricHistory,
              ...response.data.obstetric,
            };
          }
        } else {
          this.error = response.message || "Failed to load patient profile";
        }
      } catch (err) {
        console.error("Error fetching patient profile:", err);
        this.error =
          "An error occurred while loading the patient profile. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    showEditProfileModal() {
      this.showEditModal = true;
    },

    handleCloseEditModal() {
      this.showEditModal = false;
    },

    async handleSaveProfile() {
      this.showEditModal = false;

      // Refresh the profile data after saving
      await this.fetchPatientProfile();

      // Optionally show a success message
      // alert('Profile updated successfully');
    },

    getOutcomeClass(outcome) {
      const classes = {
        "Live Birth": "outcome-live",
        Stillbirth: "outcome-stillbirth",
        Miscarriage: "outcome-miscarriage",
        Abortion: "outcome-miscarriage",
      };
      return classes[outcome] || "";
    },

    formatDate(date) {
      if (!date) return "N/A";
      // Format date as needed, e.g., "15 Jun 2023"
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(date).toLocaleDateString("en-US", options);
    },
  },
};
</script>

<style scoped>
/* Loading and Error States */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 10px;
  margin-top: 20px;
}

.loading-container i {
  font-size: 3rem;
  color: #667eea;
  margin-bottom: 1rem;
}

.loading-container p {
  color: #6c757d;
  font-size: 16px;
}

.error-container i {
  font-size: 3rem;
  color: #dc3545;
  margin-bottom: 1rem;
}

.error-container p {
  color: #dc3545;
  font-size: 16px;
  margin-bottom: 1.5rem;
}

.btn-retry {
  background: #667eea;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-retry:hover {
  background: #5a6fd6;
  transform: translateY(-1px);
}

/* Reset & Base */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.container-fluid {
  padding: 12px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.main-content {
  margin-top: 12px;
}

/* Cards */
.card {
  background: white;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

/* Card Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 2px solid #dee2e6;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-header h3 i {
  color: #667eea;
  font-size: 18px;
}

/* Edit Button in Header */
.btn-edit-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 7px 16px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  white-space: nowrap;
}

.btn-edit-header:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
}

.btn-edit-header:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(102, 126, 234, 0.2);
}

.btn-edit-header i {
  font-size: 12px;
}

/* Card Body */
.card-body {
  padding: 16px 20px;
}

/* Info List */
.info-list {
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f3f4;
  gap: 15px;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row-full {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.info-row-full .value {
  text-align: left;
}

.label {
  color: #6c757d;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  min-width: 130px;
}

.value {
  color: #212529;
  font-size: 13px;
  font-weight: 500;
  text-align: right;
  word-break: break-word;
}

/* Pregnancy Stats */
.pregnancy-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 16px;
}

.stat-item {
  text-align: center;
  padding: 12px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
}

.stat-label {
  display: block;
  font-size: 10px;
  font-weight: 500;
  opacity: 0.9;
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.2;
}

/* Pregnancy Details */
.pregnancy-details {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #f1f3f4;
  gap: 10px;
}

.detail-label {
  color: #6c757d;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.detail-value {
  color: #212529;
  font-size: 13px;
  font-weight: 500;
  text-align: right;
}

.status-negative {
  color: #28a745;
  font-weight: 600;
}

.status-positive {
  color: #dc3545;
  font-weight: 600;
}

/* GP Score */
.gp-score {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 20px;
}

.gp-item {
  text-align: center;
  padding: 14px 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  transition: transform 0.2s ease;
}

.gp-item:hover {
  transform: translateY(-2px);
}

.gp-label {
  display: block;
  font-size: 11px;
  font-weight: 500;
  opacity: 0.9;
  margin-bottom: 3px;
  text-transform: uppercase;
}

.gp-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
}

/* Section Title */
.section-title {
  font-size: 14px;
  color: #495057;
  margin-bottom: 10px;
  font-weight: 600;
  padding-bottom: 6px;
  border-bottom: 2px solid #f1f3f4;
}

/* Custom Table */
.table-responsive {
  overflow-x: auto;
}

.table-custom {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.table-custom th {
  background: #f8f9fa;
  padding: 10px 8px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.table-custom td {
  padding: 8px;
  border-bottom: 1px solid #e9ecef;
  color: #495057;
}

.table-custom tbody tr:hover {
  background: #f8f9fa;
}

/* Outcome Badges */
.badge-outcome {
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
}

.outcome-live {
  background: #d4edda;
  color: #155724;
}

.outcome-stillbirth {
  background: #f8d7da;
  color: #721c24;
}

.outcome-miscarriage {
  background: #fff3cd;
  color: #856404;
}

/* Complication Badge */
.complication-badge {
  color: #dc3545;
  font-weight: 600;
  font-size: 11px;
}

.complication-badge i {
  margin-right: 3px;
}

.text-muted {
  color: #6c757d;
  font-size: 11px;
}

/* No Data */
.no-data {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
  background: #f8f9fa;
  border-radius: 6px;
}

/* Responsive */
@media (max-width: 768px) {
  .card-header {
    padding: 10px 14px;
    flex-wrap: wrap;
    gap: 8px;
  }

  .card-header h3 {
    font-size: 15px;
  }

  .btn-edit-header {
    padding: 6px 14px;
    font-size: 11px;
    width: 100%;
    justify-content: center;
  }

  .card-body {
    padding: 12px 14px;
  }

  .info-row {
    padding: 6px 8px;
    gap: 12px;
  }

  .label {
    font-size: 11px;
    min-width: 100px;
  }

  .value {
    font-size: 12px;
  }

  .pregnancy-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }

  .pregnancy-details {
    grid-template-columns: 1fr;
  }

  .gp-score {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }

  .gp-item {
    padding: 12px 6px;
  }

  .gp-value {
    font-size: 20px;
  }

  .table-custom th,
  .table-custom td {
    padding: 6px;
    font-size: 11px;
  }
}

@media (max-width: 576px) {
  .container-fluid {
    padding: 8px;
  }

  .stat-value {
    font-size: 14px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }

  .value {
    text-align: left;
  }
}
</style>
